import { useEffect, useState } from "react";

import { ensureCsrfToken } from "../../../api";



import Swal from "sweetalert2";

export default function GLCodesView() {
  const [glCode, setGlCode] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const downloadTemplate = () => {
    const csv = "GL Code,Description\n";

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "GL_Code_Template.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  async function loadGlCodes() {
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/unallocatedcost-gl-list/", {
        credentials: "include",
      });
      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Unable to load GL codes.");
      }

      setRows(payload.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load GL codes.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadGlCodes();
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    if (!file && !(glCode.trim() && description.trim())) {
      setSaving(false);

      await Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Enter a GL code and description, or upload an Excel file.",
      });

      return;
    }

    try {
      const csrfToken = await ensureCsrfToken();
      const formData = new FormData();

      if (glCode.trim()) {
        formData.append("gl_code", glCode.trim());
      }

      if (description.trim()) {
        formData.append("description", description.trim());
      }

      if (file) {
        formData.append("gl_file", file);
      }

      const response = await fetch("/api/unallocatedcost-gl-save/", {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        body: formData,
      });

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || "Unable to save GL code.");
      }

      setGlCode("");
      setDescription("");
      setFile(null);

      await loadGlCodes();

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: payload.message || "GL Code saved successfully.",
        confirmButtonText: "OK",
      });

    } catch (requestError) {

      await Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          requestError.message ||
          "Unable to save GL code.",
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
        "Unable to save GL code."
      );

    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(code) {
    setError("");

    const result = await Swal.fire({
      title: "Delete GL Code?",
      text: `Are you sure you want to delete GL code "${code}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch(
        `/delete-gl/${encodeURIComponent(code)}/`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "X-CSRFToken": csrfToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Unable to delete GL code.");
      }

      await loadGlCodes();

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: `GL code ${code} deleted successfully.`,
        confirmButtonText: "OK",
      });

    } catch (requestError) {

      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          requestError.message ||
          "Unable to delete GL code.",
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
        "Unable to delete GL code."
      );
    }
  }

  return (
    <section className="gl-view">
      <div className="workspace-panel upload-view__hero">
        <div>
          <p className="eyebrow">GL Codes</p>
          <h2>Unallocated cost GL list</h2>
          <p className="config-view__intro">
            Add GL codes manually, upload them from Excel, and manage the list used for unallocated
            cost handling.
          </p>
        </div>
      </div>

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <div className="gl-layout">
        <form className="workspace-panel gl-form" onSubmit={handleSubmit}>
          <div className="config-field">
            <label>GL Code</label>
            <input
              type="text"
              value={glCode}
              onChange={(event) => setGlCode(event.target.value)}
              placeholder="e.g. 400100"
            />
          </div>

          <div className="config-field">
            <label>Description</label>
            <input
              type="text"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="e.g. Office Expenses"
            />
          </div>

          <div className="config-field">
            <label>Or Upload Excel</label>
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
            <span className="config-note">
              Expected columns: `GL Code` and `Description`
            </span>
          </div>

          <button
            type="button"
            className="ghost-button"
            onClick={downloadTemplate}
            style={{ marginTop: "10px" }}
          >
            Download Excel Format
          </button>

          <button className="primary-button" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Add GL Code"}
          </button>
        </form>

        <div className="workspace-panel">
          <h2>Existing GL Codes</h2>
          {loading ? (
            <p className="config-note">Loading existing GL codes…</p>
          ) : (
            <div className="config-table">
              <table>
                <thead>
                  <tr>
                    <th>GL Code</th>
                    <th>Description</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((row) => (
                      <tr key={row.code}>
                        <td>{row.code}</td>
                        <td>{row.description}</td>
                        <td>
                          <button
                            className="danger-button"
                            type="button"
                            onClick={() => handleDelete(row.code)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3">No GL codes found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
